import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../api/axiosConfig';
import './OrganizationModals.css';

const CreateOrganizationModal = ({ show, parentNode, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        name: '',
        type: 'branch',
        target: '',
        managerIds: []
    });
    const [managers, setManagers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (show) {
            fetchManagers();
            // Set default type based on parent
            if (parentNode) {
                const defaultType = parentNode.type === 'root' ? 'company' : 'branch';
                setFormData(prev => ({ ...prev, type: defaultType }));
            } else {
                // No parent means creating root organization
                setFormData(prev => ({ ...prev, type: 'root' }));
            }
        }
    }, [show, parentNode]);

    const fetchManagers = async () => {
        try {
            const response = await axiosInstance.get('/api/users', {
                params: { role: 'sales_manager', active: true }
            });
            if (response.data.success) {
                setManagers(response.data.users || []);
            }
        } catch (err) {
            console.error('Error fetching managers:', err);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const payload = {
                name: formData.name,
                type: formData.type,
                target: parseFloat(formData.target),
                parentId: parentNode?._id || null,
                managerIds: formData.managerIds
            };

            const response = await axiosInstance.post('/api/sales-target/organizations', payload);

            if (response.data.success) {
                // Also persist target into monthly OrganizationTarget collection (current month/year)
                const organization = response.data.data;
                const now = new Date();
                const month = now.getMonth() + 1;
                const year = now.getFullYear();

                if (organization && organization._id && !isNaN(payload.target)) {
                    try {
                        await axiosInstance.post('/api/sales-target/organization-targets', {
                            organizationId: organization._id,
                            month,
                            year,
                            target: payload.target
                        });
                    } catch (targetErr) {
                        console.error('Failed to create organization target record:', targetErr);
                    }
                }

                onSuccess();
                handleClose();
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create organization');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({ name: '', type: 'branch', target: '', managerIds: [] });
        setError(null);
        onClose();
    };

    const toggleManager = (id) => {
        setFormData(prev => ({
            ...prev,
            managerIds: prev.managerIds.includes(id)
                ? prev.managerIds.filter(m => m !== id)
                : [...prev.managerIds, id]
        }));
    };

    if (!show) return null;

    return (
        <div className="modal-overlay" onClick={handleClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h2>Create New Organization</h2>
                {parentNode && (
                    <div className="parent-info">
                        <span>Parent: <strong>{parentNode.name}</strong></span>
                    </div>
                )}

                {error && <div className="error-message">{error}</div>}

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Name *</label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="Enter organization name"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label>Type *</label>
                        <select
                            value={formData.type}
                            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                            required
                        >
                            {!parentNode && <option value="root">Root</option>}
                            {parentNode?.type === 'root' && <option value="company">Company</option>}
                            {(parentNode?.type === 'company' || parentNode?.type === 'branch') && (
                                <option value="branch">Branch</option>
                            )}
                        </select>
                        <small>
                            {formData.type === 'root' && 'Top-level organization'}
                            {formData.type === 'company' && 'Company under root'}
                            {formData.type === 'branch' && 'Branch under company'}
                        </small>
                    </div>

                    <div className="form-group">
                        <label>Target Amount *</label>
                        <input
                            type="number"
                            value={formData.target}
                            onChange={(e) => setFormData({ ...formData, target: e.target.value })}
                            placeholder="Enter target amount"
                            min="0"
                            step="0.01"
                            required
                        />
                        <small>Monthly sales target for this organization</small>
                    </div>

                    <div className="form-group">
                        <label>Managers (Optional)</label>
                        <div className="manager-checkbox-list">
                            {managers.length === 0 && (
                                <small>No sales managers available</small>
                            )}
                            {managers.map(manager => (
                                <label key={manager._id} className="manager-checkbox-item">
                                    <input
                                        type="checkbox"
                                        checked={formData.managerIds.includes(manager._id)}
                                        onChange={() => toggleManager(manager._id)}
                                    />
                                    <span>{manager.fullName || manager.username}</span>
                                </label>
                            ))}
                        </div>
                        <small>Select one or more managers. Each can access this organization's data.</small>
                    </div>

                    <div className="modal-actions">
                        <button type="button" onClick={handleClose} className="btn-secondary" disabled={loading}>
                            Cancel
                        </button>
                        <button type="submit" className="btn-primary" disabled={loading}>
                            {loading ? 'Creating...' : 'Create Organization'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateOrganizationModal;
